import { useQuery } from '@tanstack/react-query';
import { AlertTriangle, ExternalLink } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import { Answer } from '@/components/common/Answer';
import { Snippet } from '@/components/common/Snippet';
import { SkeletonLoader } from '@/components/common/StateViews';
import { useRecordingLabel } from '@/hooks/useRecordingLabel';
import { audioAt, clock } from '@/lib/audio';
import { fetchCadeCaseAudio } from '@/services/cade';
import type {
  CadeCaseAudioValue,
  CadeCaseTalk,
  CadeSpokenTerm,
  CadeTopicPassage,
} from '@/services/types';

const TOPICS = ['market', 'harm', 'remedy', 'settlement'] as const;
/** Passages shown per topic before the rest fold behind "show more". */
const SHOWN = 3;

/**
 * What the recorded session said about THIS case.
 *
 * The case is placed by the president's call of its pauta item, confirmed by what follows
 * (its relator, its type, or the short form of its number); the stretch from that call to the
 * next is the item, and the passages inside it are where a topic is mentioned. It is machine
 * transcript, never the written vote: the notice says so before any passage, every passage
 * links to the audio at its second, and a case the audio cannot place is said to be so, with
 * how often an item can be placed at all.
 */
export function CaseAudioPanel({ caseKey }: { caseKey: string }) {
  const { t } = useTranslation(['cade']);
  const { data, isPending, error } = useQuery({
    queryKey: ['cade', 'case', caseKey, 'audio'],
    queryFn: () => fetchCadeCaseAudio(caseKey),
    retry: false,
  });

  if (isPending) return <SkeletonLoader rows={2} />;
  // A failed read of the recordings must not break the case page: the panel just stays out.
  if (error) return null;

  const population = data.audio.population;
  return (
    <section className="space-y-3 rounded-lg border bg-card p-4" data-case-audio>
      <h2 className="text-sm font-semibold text-foreground">{t('cade:case.audio.title')}</h2>
      <p
        role="note"
        className="flex items-start gap-2 rounded-md border border-amber-500/40 bg-amber-500/5 p-2 text-xs text-foreground"
        data-machine-transcript
      >
        <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-amber-600" aria-hidden />
        {t('cade:case.audio.notice')}
      </p>
      <Answer answer={data.audio} scope="record" help={t('cade:case.audio.help')}>
        {(value: CadeCaseAudioValue) => (
          <ul className="space-y-3">
            {value.talks.map((talk) => (
              <Talk key={`${talk.recording.key}-${talk.pauta_item}`} talk={talk} />
            ))}
          </ul>
        )}
      </Answer>
      <p className="text-xs text-muted-foreground" data-case-audio-coverage>
        {t('cade:case.audio.coverage', { placed: population.measured, total: population.total })}
      </p>
    </section>
  );
}

function Talk({ talk }: { talk: CadeCaseTalk }) {
  const { t } = useTranslation(['cade']);
  const label = useRecordingLabel();
  const link = audioAt(talk.recording.audio_url, talk.start_s);
  const judged = talk.status === 'judged';
  return (
    <li className="space-y-2 rounded-md border p-3" data-case-talk data-status={talk.status}>
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-muted-foreground">
        <Link
          to={`/cade/audio/${talk.recording.key}`}
          className="font-medium text-primary underline-offset-4 hover:underline"
        >
          {label(talk.recording)}
        </Link>
        <span>{t('cade:case.audio.item', { number: talk.pauta_item })}</span>
        <span className="rounded bg-muted px-1.5 py-0.5 text-foreground">
          {t(`cade:case.audio.status.${talk.status}`)}
        </span>
        {judged && talk.confidence ? (
          <span data-confidence={talk.confidence}>
            {t(`cade:case.audio.confidence.${talk.confidence}`)}
          </span>
        ) : null}
        {judged ? (
          <span className="tabular-nums">
            {clock(talk.start_s)}–{clock(talk.end_s)}
          </span>
        ) : null}
      </div>
      <blockquote className="text-sm italic text-foreground">“{talk.call_quote}”</blockquote>
      {talk.joint && judged ? (
        <p className="text-xs text-muted-foreground" data-joint>
          {t('cade:case.audio.joint')}
        </p>
      ) : null}
      <div className="flex flex-wrap items-center gap-3 text-xs">
        {link ? (
          <a
            href={link}
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center gap-1 text-primary underline-offset-4 hover:underline"
          >
            <ExternalLink className="h-3 w-3" aria-hidden />
            {t('cade:audio.listen', { time: clock(talk.start_s) })}
          </a>
        ) : null}
        <Link
          to={`/cade/audio/${talk.recording.key}/transcript#seg-${talk.seg_from}`}
          className="text-primary underline-offset-4 hover:underline"
        >
          {t('cade:case.audio.seeText')}
        </Link>
      </div>
      {judged ? (
        <SpokenTerms
          terms={talk.spoken ?? []}
          audioUrl={talk.recording.audio_url}
          joint={talk.joint}
        />
      ) : null}
      {judged ? <Passages passages={talk.passages} /> : null}
    </li>
  );
}

function Passages({ passages }: { passages: CadeTopicPassage[] }) {
  const { t } = useTranslation(['cade']);
  const present = TOPICS.filter((topic) => passages.some((p) => p.topic === topic));
  if (present.length === 0) {
    return <p className="text-xs text-muted-foreground">{t('cade:case.audio.noPassages')}</p>;
  }
  return (
    <div className="space-y-2" data-case-passages>
      {present.map((topic) => {
        const own = passages.filter((p) => p.topic === topic);
        return (
          <div key={topic} className="space-y-1">
            <h3 className="text-xs font-medium text-foreground">
              {t(`cade:audio.topics.names.${topic}`)}{' '}
              <span className="font-normal text-muted-foreground">({own.length})</span>
            </h3>
            <ul className="space-y-1">
              {own.slice(0, SHOWN).map((passage) => (
                <Passage key={passage.seg} passage={passage} />
              ))}
            </ul>
            {own.length > SHOWN ? (
              <details>
                <summary className="cursor-pointer text-xs text-primary">
                  {t('cade:case.audio.more', { count: own.length - SHOWN })}
                </summary>
                <ul className="mt-1 space-y-1">
                  {own.slice(SHOWN).map((passage) => (
                    <Passage key={passage.seg} passage={passage} />
                  ))}
                </ul>
              </details>
            ) : null}
          </div>
        );
      })}
    </div>
  );
}

function Passage({ passage }: { passage: CadeTopicPassage }) {
  const link = audioAt(passage.recording.audio_url, passage.start_s);
  return (
    <li className="text-xs text-foreground" data-case-passage>
      {link ? (
        <a
          href={link}
          target="_blank"
          rel="noreferrer"
          className="mr-2 tabular-nums text-primary underline underline-offset-4"
        >
          {clock(passage.start_s)}
        </a>
      ) : null}
      <Snippet text={passage.text_marked} />
    </li>
  );
}

const TERM_ORDER = [
  'contribution_amount',
  'structural_remedy',
  'behavioural_remedy',
  'trustee',
  'duration',
] as const;

/**
 * TCC and remedy terms as HEARD in the item's stretch: a contribution amount beside the words
 * "contribuição pecuniária", a remedy word, a trustee, a duration beside a commitment word. Each
 * is a lead with the words it came from and a link to the second, never a coded field.
 */
function SpokenTerms({
  terms,
  audioUrl,
  joint,
}: {
  terms: CadeSpokenTerm[];
  audioUrl: string | null;
  joint: boolean;
}) {
  const { t, i18n } = useTranslation(['cade']);
  if (terms.length === 0) return null;
  const ordered = [...terms].sort(
    (a, b) => TERM_ORDER.indexOf(a.kind) - TERM_ORDER.indexOf(b.kind) || a.start_s - b.start_s,
  );
  const money = new Intl.NumberFormat(i18n.language, {
    style: 'currency',
    currency: 'BRL',
    maximumFractionDigits: 2,
  });
  const value = (term: CadeSpokenTerm): string | null => {
    if (term.value === null) return null;
    if (term.unit === 'BRL') return money.format(term.value);
    return t('cade:case.audio.spoken.months', { count: term.value });
  };
  return (
    <div className="space-y-1" data-spoken-terms>
      <h3 className="text-xs font-medium text-foreground">{t('cade:case.audio.spoken.title')}</h3>
      <p className="text-xs text-muted-foreground">{t('cade:case.audio.spoken.notice')}</p>
      {joint ? (
        <p className="text-xs text-muted-foreground">{t('cade:case.audio.spoken.joint')}</p>
      ) : null}
      <ul className="space-y-1">
        {ordered.map((term) => {
          const link = audioAt(audioUrl, term.start_s);
          const amount = value(term);
          return (
            <li
              key={`${term.kind}-${term.seg}-${term.text_marked}`}
              className="text-xs text-foreground"
              data-spoken-term={term.kind}
            >
              <span className="font-medium">{t(`cade:case.audio.spoken.kind.${term.kind}`)}</span>
              {amount ? <span className="ml-1 tabular-nums">{amount}</span> : null}
              {term.ruling ? (
                <span className="ml-1 rounded bg-primary/10 px-1.5 py-0.5">
                  {t('cade:case.audio.spoken.ruling')}
                </span>
              ) : null}
              {link ? (
                <a
                  href={link}
                  target="_blank"
                  rel="noreferrer"
                  className="ml-2 tabular-nums text-primary underline underline-offset-4"
                >
                  {clock(term.start_s)}
                </a>
              ) : null}
              <div className="text-muted-foreground">
                <Snippet text={term.text_marked} />
              </div>
            </li>
          );
        })}
      </ul>
    </div>
  );
}
